// src/components/HRComponents/PolicyPerformanceTabs.js
import React, { useMemo } from "react";
import { LayoutDashboard, FileText, FolderOpen, UserCheck } from "lucide-react";
import { useTabNavigation } from "../../helpers/useTabNavigation";
import { usePermission } from "../../hooks/usePermission";

// Importing the completed components
import PolicyList from "./PolicyList";
import PolicyCategoryManager from "../../components/HRComponents/PolicyCategoryManager";
import EmployeePolicyInbox from "../../components/HRComponents/EmployeePolicyInbox";
import PolicyDashboard from "../../components/HRComponents/PolicyDashboard";


const PolicyTabs = () => {
  const { hasPermission, user } = usePermission();

  const tabs = useMemo(() => {
    // 1. Standard Tabs - Accessible to all logged-in employees
    const allTabs = [
      {
        id: "my-policies",
        label: "Organization Policies",
        icon: <UserCheck className="h-4 w-4" />,
        // description: "View and acknowledge policies assigned to you",
        content: <EmployeePolicyInbox user={user} />,
      }
    ];

    // 2. Admin/HR Tabs - Conditional access based on permissions or Owner status
    const adminTabs = [
      {
        id: "policy-dashboard",
        label: "Dashboard",
        icon: <LayoutDashboard className="h-4 w-4" />,
        // description: "Analytics and status summary for all policies",
        content: <PolicyDashboard user={user} />,
        permissionKey: "hr:policies",
        action: "view"
      },
      {
        id: "policies-letters",
        label: "Policies & Letters",
        icon: <FileText className="h-4 w-4" />,
        // description: "Create and manage organizational policies & employee letters",
        content: <PolicyList user={user} />,
        permissionKey: "hr:policies",
        action: "view"
      },
      {
        id: "manage-categories",
        label: "Manage Categories",
        icon: <FolderOpen className="h-4 w-4" />,
        // description: "Organize policies and letters with custom categories",
        content: <PolicyCategoryManager user={user} />,
        permissionKey: "hr:policies",
        action: "view"
      }
    ];

    // Check permissions dynamically and push allowed Admin tabs
    adminTabs.forEach(tab => {
      // Owner bypasses all permissions checks
      const isOwner = user?.role === "owner";

      if (isOwner || hasPermission(tab.permissionKey, tab.action)) {
        allTabs.push(tab);
      }
    });

    return allTabs;
  }, [user, hasPermission]);

  // Handle active tab state navigation, defaulting to "my-policies" for employees, 
  // or "policy-dashboard" for HR admins if permissions are met.
  const defaultTab = useMemo(() => {
    const isOwner = user?.role === "owner";
    const hasAdminAccess = isOwner || hasPermission("hr:policies", "view");
    return hasAdminAccess ? "policy-dashboard" : "my-policies";
  }, [user, hasPermission]);

  const { activeTab, changeTab } = useTabNavigation(tabs, defaultTab);
  const activeTabData = tabs.find(tab => tab.id === activeTab) || tabs[0];

  return (
    <div>
      {/* --- TOP TAB NAVIGATION BAR --- */}
      <div className="bg-primary shadow-md sticky top-0 z-20">
        <div className="mx-auto">
          <div className="px-4 lg:px-6">
            <div className="flex items-center justify-between">
              <nav className="flex space-x-2 overflow-x-auto custom-scrollbar pt-2" aria-label="Tabs">
                {tabs.map((tab) => {
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => changeTab(tab.id)}
                      className={`
                        group inline-flex items-center py-3 px-6 font-bold text-[11px] uppercase tracking-[0.1em]
                        whitespace-nowrap transition-all duration-300 ease-in-out
                        ${isActive
                          ? 'bg-white text-primary rounded-t-xl transform -translate-y-px shadow-sm'
                          : 'text-red-100 hover:text-white hover:bg-red-700 rounded-t-xl'
                        }
                      `}
                    >
                      <span className="mr-2 opacity-70">{tab.icon}</span>
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </nav>

              <div className="hidden lg:block text-[10px] font-bold text-red-100 uppercase tracking-widest opacity-80 italic">
                {activeTabData?.description}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* --- CONTENT DISPLAY AREA --- */}
      <div className="mx-auto py-6">
        <div className="max-w-[1600px] mx-auto animate-in fade-in duration-500">
          {activeTabData?.content}
        </div>
      </div>
    </div>
  );
};

export default PolicyTabs;